import React, {
  forwardRef,
  TextareaHTMLAttributes,
  ChangeEvent,
  JSX,
} from 'react'
import { COLORS, FONTS, SPACINGS, BREAKPOINTS } from './theme'
import { Typography } from '@mui/material'
import { Label } from './label'
import styled, { css } from 'styled-components'

const typographyStyles = {
  headline1: css`
    @media only screen and (${BREAKPOINTS.sm}) {
      font-size: ${FONTS.sizes.xxxl};
    }
    font-size: ${FONTS.sizes.xl};
    font-family: ${FONTS.families.headlines.light};
    color: ${COLORS.secondary.darkBlue.primary};
    font-weight: ${FONTS.weights.light};
  `,
  headline2: css`
    @media only screen and (${BREAKPOINTS.sm}) {
      font-size: ${FONTS.sizes.xl};
    }
    font-family: ${FONTS.families.headlines.regular};
    font-weight: ${FONTS.weights.normal};
    color: ${COLORS.secondary.darkBlue.primary};
    font-size: ${FONTS.sizes.lg};
  `,
  headline3: css`
    font-size: ${FONTS.sizes.lg};
    font-family: ${FONTS.families.headlines.regular};
    font-weight: ${FONTS.weights.normal};
    color: ${COLORS.secondary.darkBlue.primary};
  `,
  headline4: css`
    font-size: ${FONTS.sizes.md};
    font-family: ${FONTS.families.headlines.regular};
    font-weight: ${FONTS.weights.normal};
    color: ${COLORS.secondary.darkBlue.primary};
  `,
  link: css`
    font-size: ${FONTS.sizes.sm};
    font-family: ${FONTS.families.regular};
    color: ${COLORS.primary.maerskBlue};
  `,
  button: css`
    font-size: ${FONTS.sizes.sm};
    font-family: ${FONTS.families.regular};
    color: ${COLORS.secondary.darkBlue.primary};
  `,
  paragraph1: css`
    font-size: ${FONTS.sizes.md};
    font-family: ${FONTS.families.regular};
    color: ${COLORS.secondary.darkBlue.primary};
  `,
  paragraph1Secondary: css`
    font-size: ${FONTS.sizes.md};
    font-family: ${FONTS.families.regular};
    color: ${COLORS.greys.dark};
  `,
  paragraphBold1: css`
    font-size: ${FONTS.sizes.md};
    font-family: ${FONTS.families.bold};
    font-weight: ${FONTS.weights.bold};
    color: ${COLORS.secondary.darkBlue.primary};
  `,
  paragraphBoldLarge: css`
    font-size: ${FONTS.sizes.lg};
    font-family: ${FONTS.families.bold};
    font-weight: ${FONTS.weights.bold};
    color: ${COLORS.secondary.darkBlue.primary};
  `,
  paragraph2: css`
    font-size: ${FONTS.sizes.sm};
    font-family: ${FONTS.families.regular};
    color: ${COLORS.secondary.darkBlue.primary};
  `,
  paragraphBold2: css`
    font-size: ${FONTS.sizes.sm};
    font-family: ${FONTS.families.bold};
    font-weight: ${FONTS.weights.bold};
    color: ${COLORS.secondary.darkBlue.primary};
  `,
  caption: css`
    font-size: ${FONTS.sizes.xs};
    font-family: ${FONTS.families.regular};
    color: ${COLORS.secondary.darkBlue.primary};
  `,
  menu: css`
    font-size: ${FONTS.sizes.xs};
    font-family: ${FONTS.families.headlines.regular};
    color: ${COLORS.secondary.darkBlue.primary};
    text-transform: uppercase;
  `,
  label: css`
    font-size: ${FONTS.sizes.xs};
    font-family: ${FONTS.families.headlines.bold};
    font-weight: ${FONTS.weights.bold};
    color: ${COLORS.secondary.darkBlue.primary};
    text-transform: uppercase;
  `,
  graphCaption: css`
    font-size: ${FONTS.sizes.xxs};
    font-family: ${FONTS.families.regular};
    color: ${COLORS.greys.dark};
    text-transform: uppercase;
  `,
}

export type TypographyVariant =
  | 'headline1'
  | 'headline2'
  | 'headline3'
  | 'headline4'
  | 'link'
  | 'button'
  | 'paragraph1'
  | 'paragraph1Secondary'
  | 'paragraphBold1'
  | 'paragraphBoldLarge'
  | 'paragraph2'
  | 'paragraphBold2'
  | 'caption'
  | 'menu'
  | 'label'
  | 'graphCaption'

export interface TypographyProps {
  as?: keyof JSX.IntrinsicElements
  variant: TypographyVariant
  children: React.ReactNode
  className?: string
  color?: string
}

const variantToElementMap: {
  [variant in TypographyVariant]: keyof JSX.IntrinsicElements
} = {
  headline1: 'h1',
  headline2: 'h2',
  headline3: 'h3',
  headline4: 'h4',
  link: 'a',
  button: 'span',
  paragraph1: 'p',
  paragraph1Secondary: 'p',
  paragraphBold1: 'p',
  paragraphBoldLarge: 'p',
  paragraph2: 'p',
  paragraphBold2: 'p',
  caption: 'span',
  menu: 'nav',
  label: 'label',
  graphCaption: 'figcaption',
}

export const variantToStylesMap: {
  [variant in TypographyVariant]: ReturnType<typeof css>
} = {
  headline1: typographyStyles.headline1,
  headline2: typographyStyles.headline2,
  headline3: typographyStyles.headline3,
  headline4: typographyStyles.headline4,
  link: typographyStyles.link,
  button: typographyStyles.button,
  paragraph1: typographyStyles.paragraph1,
  paragraph1Secondary: typographyStyles.paragraph1Secondary,
  paragraphBold1: typographyStyles.paragraphBold1,
  paragraphBoldLarge: typographyStyles.paragraphBoldLarge,
  paragraph2: typographyStyles.paragraph2,
  paragraphBold2: typographyStyles.paragraphBold2,
  caption: typographyStyles.caption,
  menu: typographyStyles.menu,
  label: typographyStyles.label,
  graphCaption: typographyStyles.graphCaption,
}

export const StyledTypography = styled.div<TypographyProps>`
  ${({ variant }) => variantToStylesMap[variant]}
  ${({ color }) => color && `color: ${color};`}
`

export const FieldError = styled.div`
  position: absolute;
  font-family: ${FONTS.families.regular};
  color: ${COLORS.feedback.red.error};
  font-size: ${FONTS.sizes.xs};
  padding-left: ${SPACINGS[2]};
  top: calc(100% + ${SPACINGS[1]});
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  name: string
  onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void
  value?: string
  id?: string
  disabled?: boolean
  description?: string
  error?: string
}

interface StyledTextAreaProps {
  hasError?: boolean
  hasIcon?: boolean
}

export const StyledTextArea = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 448px;

  ${StyledTypography} {
    margin-bottom: ${SPACINGS[2]};
  }
`

const StyledTextAreaField = styled.textarea<StyledTextAreaProps>`
  position: relative;
  background-color: ${COLORS.primary.white};
  border-radius: 4px;
  box-sizing: border-box;
  border: 1px solid ${COLORS.secondary.darkBlue.primary};
  border-color: ${(p) => p.hasError && COLORS.feedback.red.error};
  color: ${COLORS.secondary.darkBlue.primary};
  font-size: ${FONTS.sizes.sm};
  font-family: ${FONTS.families.regular};
  min-width: 250px;
  height: 112px;
  padding: ${SPACINGS[2]};
  padding-left: ${(p) => (p.hasIcon ? SPACINGS[6] : SPACINGS[2])};
  resize: none;

  &::placeholder {
    color: ${COLORS.greys.mid};
  }

  &:focus-visible {
    outline: none;
    border: 1px solid ${COLORS.primary.maerskBlue};
  }

  &:hover {
    background-color: ${COLORS.secondary.lightBlue.tint};
    border-color: transparent;
    &::placeholder {
      color: ${COLORS.secondary.darkBlue.primary};
    }
  }

  &:disabled {
    color: ${COLORS.greys.mid};
    border-color: ${COLORS.greys.mid};
    pointer-events: none;
  }
`

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      disabled,
      error,
      label,
      description,
      name,
      onChange,
      value,
      required,
      ...props
    },
    ref,
  ) => (
    <StyledTextArea>
      {label && (
        <Label
          htmlFor={name}
          disabled={Boolean(disabled)}
          label={label}
          required={required}
        />
      )}

      {description && <Typography variant="caption">{description}</Typography>}

      <StyledTextAreaField
        name={name}
        id={name}
        required={required}
        ref={ref}
        disabled={disabled}
        hasError={!!error}
        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
          if (onChange) {
            onChange(e)
          }
        }}
        value={value}
        {...props}
      />

      {error && <FieldError>{error}</FieldError>}
    </StyledTextArea>
  ),
)

TextArea.displayName = 'TextArea'

export default TextArea
